<script setup>
import { defineEmits } from 'vue'
import { InfoWindow } from 'vue3-google-map'
import Link from '@/Components/Links/Link.vue'
import CloseButton from '@/Components/Buttons/CloseButton.vue'
import AnchorArrow from '@svgs/anchor-arrow.svg'
import ServiceLogo from '@/Components/Maps/ServiceLogo.vue'
import { usePagination } from '@/Composables/usePagination.js'

defineEmits(['close'])

const props = defineProps({
  position: {
    type: Object,
    required: true,
  },
  services: {
    type: Array,
    required: true,
  },
})
const { paginatedItems, currentPage, totalPages, nextPage, prevPage } = usePagination(props.services)
</script>

<template>
  <InfoWindow
    :options="{ position: position, pixelOffset: { width: 0, height: -50 } }"
    @closeclick="$emit('close')"
    id="info-window"
  >
    <div class="flex flex-col items-center divide-y divide-gray-200 rounded-md w-full">
      <div
        class="flex w-full flex-col md:flex-row md:items-center justify-between p-0 py-3 md:p-3 overflow-hidden"
        v-for="service in paginatedItems"
      >
        <div class="flex items-center w-full">
          <ServiceLogo
            :logo="service.logo"
            :initials="service.initials"
            class="self-start md:self-center"
          />

          <div class="mx-2 md:mx-4 flex-grow truncate">
            <h4 class="truncate text-base font-bold max-w-[250px]">
              {{ service.title }}
            </h4>

            <Link
              class="!text-black cursor-pointer !focus:bg-yellow"
              v-if="service.website"
              :href="service.website.startsWith('http') ? service.website : 'https://' + service.website"
              target="_blank"
            >
              {{ service.website }}
            </Link>
            <Link
              class="text-base font-medium flex items-center mt-2 md:mt-0 md:hidden flex"
              :href="`/services/${service.slug}`"
            >
              View profile
              <AnchorArrow class="ml-2" />
            </Link>
          </div>
          <Link
            class="text-base font-medium flex items-center mt-2 md:mt-0 hidden md:flex"
            :href="`/services/${service.slug}`"
          >
            View profile
            <AnchorArrow class="ml-2" />
          </Link>
        </div>
      </div>

      <div
        class="flex justify-end w-full p-2"
        v-if="totalPages > 1"
      >
        <Link
          @click="prevPage"
          :disabled="currentPage === 1"
          class="px-2 py-1 disabled:text-grey-800 flex items-center text-base font-medium"
          href="javascript:doNothing;"
        >
          <AnchorArrow class="mr-2 rotate-180 focus:stroke-black" />
          Previous
        </Link>
        <Link
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="px-2 py-1 disabled:text-grey-800 flex items-center text-base font-medium"
          href="javascript:doNothing;"
        >
          Next
          <AnchorArrow class="ml-2 focus:stroke-black" />
        </Link>
      </div>
    </div>
  </InfoWindow>
</template>
